/**
 * ServerStatusGrid - Enterprise Gaming Servers Display
 * Griglia real-time dei server gaming con controlli amministrativi
 */

import React, { useState } from 'react'
import { motion } from 'framer-motion'
import { useServers } from '@/hooks/useServers'
import { useAuth } from '@/hooks/useAuth'
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/components/ui/card'
import { Badge } from '@/shared/components/ui/badge'
import { Button } from '@/shared/components/ui/button'
import { Progress } from '@/shared/components/ui/progress'
import { 
  Server, 
  Users, 
  Play,
  Pause,
  RefreshCw,
  Clock,
  Settings
} from 'lucide-react'
import { formatDistanceToNow } from 'date-fns'
import { it } from 'date-fns/locale'

const getServerIcon = (type: string) => {
  switch (type.toLowerCase()) {
    case 'minecraft':
    case 'cobblemon':
      return '🟩'
    case 'rust':
      return '🦀'
    case 'ksp':
      return '🚀'
    case 'rimworld':
      return '🏕️'
    default:
      return '🎮'
  }
}

const getStatusColor = (status: string) => {
  switch (status) {
    case 'online': return 'bg-green-500'
    case 'offline': return 'bg-gray-500'
    case 'error': return 'bg-red-500'
    default: return 'bg-yellow-500'
  }
}

const getStatusBadge = (status: string) => {
  switch (status) {
    case 'online': 
      return <Badge className="bg-green-100 text-green-800 border-green-200">Online</Badge>
    case 'offline': 
      return <Badge variant="secondary">Offline</Badge>
    case 'error': 
      return <Badge variant="destructive">Errore</Badge>
    default: 
      return <Badge variant="outline">Sconosciuto</Badge>
  }
}

export const ServerStatusGrid: React.FC = () => {
  const { serversArray, isLoading, startServer, stopServer, restartServer, refresh } = useServers()
  const { isAdmin } = useAuth()
  const [actionLoading, setActionLoading] = useState<string | null>(null)

  const handleServerAction = async (serverId: string, action: 'start' | 'stop' | 'restart') => {
    setActionLoading(`${serverId}-${action}`)
    try {
      if (action === 'start') await startServer(serverId)
      if (action === 'stop') await stopServer(serverId)
      if (action === 'restart') await restartServer(serverId)
    } catch (error) {
      console.error(`Failed to ${action} server:`, error)
    } finally {
      setActionLoading(null)
    }
  }

  if (isLoading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {[...Array(6)].map((_, i) => (
          <Card key={i} className="animate-pulse">
            <CardHeader>
              <div className="h-4 bg-gray-200 rounded w-3/4"></div>
              <div className="h-3 bg-gray-200 rounded w-1/2"></div>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="h-4 bg-gray-200 rounded"></div>
                <div className="h-2 bg-gray-200 rounded"></div>
                <div className="h-8 bg-gray-200 rounded"></div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    )
  }

  if (serversArray.length === 0) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-12">
          <div className="text-center">
            <Server className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Nessun server trovato</h3>
            <p className="text-gray-600 mb-4">Nessun server gaming configurato al momento.</p>
            <Button onClick={refresh} variant="outline">
              <RefreshCw className="h-4 w-4 mr-2" />
              Aggiorna
            </Button>
          </div>
        </CardContent>
      </Card>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header Actions */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <h3 className="text-lg font-semibold">
            {serversArray.length} server
          </h3>
          <Badge variant="outline" className="text-sm">
            {serversArray.filter((s) => s.status === 'online').length} online
          </Badge>
        </div>
        <Button onClick={refresh} variant="outline" size="sm">
          <RefreshCw className="h-4 w-4 mr-2" />
          Aggiorna tutto
        </Button>
      </div>

      {/* Servers Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {serversArray.map((server, index) => {
          const isOnline = server.status === 'online'
          const hasError = server.status === 'offline'
          const playersKnown = server.players?.known !== false
          const playerPercentage = playersKnown && server.players.max > 0
            ? (server.players.current / server.players.max) * 100
            : 0
          const address = (() => {
            const host = (server.ip || '').trim()
            const port = server.port
            if (host && port) return `${host}:${port}`
            if (host) return host
            if (port) return `:${port}`
            return 'sconosciuto'
          })()
          const lastCheckDate = server.last_check ? new Date(server.last_check) : null
          const lastCheckLabel = lastCheckDate && !Number.isNaN(lastCheckDate.getTime())
            ? formatDistanceToNow(lastCheckDate, { locale: it, addSuffix: true })
            : 'N/A'

          return (
            <motion.div
              key={server.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
            >
              <Card className={`relative overflow-hidden ${
                isOnline ? 'border-green-200 bg-green-50/30' : 
                hasError ? 'border-red-200 bg-red-50/30' : 
                'border-gray-200'
              }`}>
                {/* Status Indicator */}
                <div className={`absolute top-0 left-0 w-full h-1 ${getStatusColor(server.status)}`} />
                
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="text-lg">{getServerIcon(server.game)}</span>
                      <div>
                        <CardTitle className="text-base font-semibold">
                          {server.name}
                        </CardTitle>
                        <p className="text-sm text-muted-foreground capitalize">
                          {server.game} • {address}
                        </p>
                      </div>
                    </div>
                    {getStatusBadge(server.status)}
                  </div>
                </CardHeader>

                <CardContent className="space-y-4">
                  {/* Player Count */}
                  <div className="space-y-2">
                    <div className="flex items-center justify-between text-sm">
                      <span className="flex items-center gap-1">
                        <Users className="h-4 w-4" />
                        Giocatori
                      </span>
                      <span className="font-medium">
                        {playersKnown ? (
                          `${server.players.current}/${server.players.max}`
                        ) : (
                          'N/A'
                        )}
                      </span>
                    </div>
                    <Progress value={playerPercentage} className="h-2" />
                  </div>

                  {/* Server Stats */}
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-muted-foreground">ID</span>
                      <div className="font-medium">{server.id}</div>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Ultimo check</span>
                      <div className="font-medium flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        {lastCheckLabel}
                      </div>
                    </div>
                  </div>

                  {/* Admin Actions */}
                  {isAdmin() && (
                    <div className="flex items-center gap-2 pt-2 border-t">
                      <Button
                        size="sm"
                        variant={isOnline ? "destructive" : "default"}
                        onClick={() => handleServerAction(server.id, isOnline ? 'stop' : 'start')}
                        disabled={actionLoading === `${server.id}-${isOnline ? 'stop' : 'start'}`}
                        className="flex-1"
                      >
                        {actionLoading === `${server.id}-${isOnline ? 'stop' : 'start'}` ? (
                          <RefreshCw className="h-3 w-3 mr-1 animate-spin" />
                        ) : isOnline ? (
                          <Pause className="h-3 w-3 mr-1" />
                        ) : (
                          <Play className="h-3 w-3 mr-1" />
                        )}
                        {isOnline ? 'Ferma' : 'Avvia'}
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleServerAction(server.id, 'restart')}
                        disabled={actionLoading === `${server.id}-restart`}
                        title="Riavvia"
                      >
                        <Settings
                          className={`h-3 w-3 ${actionLoading === `${server.id}-restart` ? 'animate-spin' : ''}`}
                        />
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            </motion.div>
          )
        })}
      </div>
    </div>
  )
}
